// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GridItem.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Grid item component
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { propTypes as observablePropTypes } from 'mobx-react';

import Resizable from 'share/components/controls/Izenda.Resizable';
import Movable from 'share/components/controls/Izenda.Movable';
import Measure from 'share/components/controls/Izenda.Measure';
import styleUtils from './Izenda.GridViewStyleUtils';
import layoutComputerUtils from './Izenda.LayoutComputerUtils';
import { identity, omit, assignAll, merge } from 'lodash/fp';

/**
 * Grid item animation content that showing when animating
 */
const GridItemAnimation = observer(function GridItemAnimation(props: any) {
  const {
    layoutItem,
    interpolatingStyle,
    mixinResizable,
    mixinDraggable,
    isHidden,
    targetTop,
    targetLeft,
    targetWidth,
    targetHeight,
    onMeasureChange,
    printingMode,
    isFirstItem
  } = props;

  const autoSize = !printingMode && layoutItem.autoSize;

  const position = {
    top: targetTop,
    left: targetLeft,
    width: targetWidth,
    height: targetHeight
  };

  const doesBreakPage = printingMode && props.doesBreakPage;

  if (props.fillWidth) {
    interpolatingStyle.width = `${layoutItem.gridWidth * 100 / props.cols}%`;
    interpolatingStyle.left = `${layoutItem.gridLeft * 100 / props.cols}%`;
  }

  const style = layoutItem.isFullSize
    ? styleUtils.transformToFullSizeStyle(interpolatingStyle)
    : styleUtils.transformToDefaultStyle(interpolatingStyle, true, doesBreakPage, printingMode, autoSize, isFirstItem);
  style.left += 1;
  style.top += 1;
  const className = classnames({
    u_hidden: isHidden,
    'izenda-GridItemAnimation': true
  });

  if (isHidden) {
    // Item needs to be visible, to ensure properly calculating size.
    // Keep the item displayed, but put it out of view portion.
    style.position = 'fixed';
    style.zIndex = -1000;
    style.top = -100000;
    style.left = -100000;
  }

  return mixinDraggable(
    mixinResizable(
      <div className={className} style={style}>
        <Measure onChange={onMeasureChange}>{props.children}</Measure>
      </div>,
      position
    ),
    position
  );
});

/**
 * Grid item component in grid view
 */
@observer
export default class GridItem extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    // Grid item is editable or just view only
    editable: PropTypes.bool,

    // Only one child
    children: PropTypes.element,

    // Observable grid item state
    layoutItem: observablePropTypes.observableObject,

    // Item state
    moveToMe: PropTypes.bool,

    // ScrollBox funcs
    scrollVertical: PropTypes.func,

    // Width (pixel) of container
    containerWidth: PropTypes.number,
    // Height (pixel) of container
    containerHeight: PropTypes.number,

    // Grid layout cell's dimension
    cellWidth: PropTypes.number,
    cellHeight: PropTypes.number,

    // Layout columns & rows numbers
    cols: PropTypes.number.isRequired,
    rows: PropTypes.number.isRequired,

    // Min/max size of grid item
    minWidth: PropTypes.number,
    minHeight: PropTypes.number,
    maxWidth: PropTypes.number,
    maxHeight: PropTypes.number,

    // Interactive event handlers
    onDragStop: PropTypes.func,
    onDragStart: PropTypes.func,
    onDrag: PropTypes.func,
    onResizeStop: PropTypes.func,
    onResizeStart: PropTypes.func,
    onResize: PropTypes.func,

    // Handle on size of grid item changed
    onSizeChanged: PropTypes.func,

    // Allow user drag
    isDraggable: PropTypes.bool.isRequired,
    // Allow user resize
    isResizable: PropTypes.bool.isRequired,

    // Selector for draggable handle
    handle: PropTypes.string,
    // Selector for draggable cancel (see react-draggable)
    cancel: PropTypes.string,
    // Show loading when interacting
    showLoading: PropTypes.bool,
    // Should be hidden
    isHidden: PropTypes.bool,

    // Actual width & height pixel
    width: PropTypes.number,
    height: PropTypes.number,

    layoutStore: PropTypes.any
  };

  static defaultProps = {
    cancel: '',
    cols: 1,
    rows: 1,
    minWidth: 1,
    minHeight: 1,
    cellWidth: 0,
    cellHeight: 0,
    maxWidth: Infinity,
    maxHeight: Infinity,
    handle: '[draggable="true"]',
    isHidden: false
  };

  componentWillMount() {
    const props = this.props;

    this.onMoveStart = this.onMoveHandler('onMoveStart');
    this.onMoveStop = this.onMoveHandler('onMoveStop');
    this.onMove = this.onMoveHandler('onMove');
    this.onResizeStart = this.onResizeHandler('onResizeStart');
    this.onResizeStop = this.onResizeHandler('onResizeStop');
    this.onResize = this.onResizeHandler('onResize');

    this.draggableOptions = {
      handle: props.handle,
      cancel: props.cancel
    };
  }

  componentDidMount() {
    // If i'm the recently added item, scroll to me.
    this.props.moveToMe && this.scrollToMe();
  }

  render() {

    const props = this.props;
    const {
      editable,
      layoutItem,
      showLoading,
      containerWidth,
      containerHeight,
      isHidden,
      printingMode,
      doesBreakPage,
      isFirstItem
    } = props;
    const isDraggable = props.isDraggable && editable;
    const isResizable = props.isResizable && editable;
    const onMeasureChange = this.onMeasureChange;

    // If this item is full sized, its position & size is to fit the grid view
    const itemLeft = layoutItem.isFullSize ? 0 : layoutItem.pxLeft - 1;
    const itemTop = layoutItem.isFullSize ? 0 : layoutItem.pxTop - 1;
    const itemWidth = layoutItem.isFullSize ? containerWidth : layoutItem.pxWidth + 1;
    const itemHeight = layoutItem.isFullSize ? containerHeight : layoutItem.pxHeight + 1;

    const child = this.mergeParentProps(React.Children.only(props.children));
    const mixinResizable = isResizable ? this.mixinResizable : identity;
    const mixinDraggable = isDraggable ? this.mixinDraggable : identity;

    const itemPositionStyle = {
      top: itemTop,
      left: itemLeft,
      width: itemWidth,
      height: itemHeight,
      overflow: 'scroll',
      rotate: layoutItem.isFullSize ? 180 : 0
    };

    return (
      <GridItemAnimation
        layoutItem={layoutItem}
        targetTop={itemTop}
        targetLeft={itemLeft}
        targetWidth={itemWidth}
        targetHeight={itemHeight}
        interpolatingStyle={itemPositionStyle}
        mixinResizable={mixinResizable}
        mixinDraggable={mixinDraggable}
        showLoading={showLoading}
        isHidden={isHidden}
        printingMode={printingMode}
        doesBreakPage={doesBreakPage}
        onMeasureChange={onMeasureChange}
        isFirstItem={isFirstItem}
        fillWidth={props.fillWidth}
        cols={props.cols}
      >
        {child}
      </GridItemAnimation>
    );
  }

  /**
   * Extend child element to be draggable
   * @param {any} child Child element
   * @param {object} pos Element position
   */
  mixinDraggable = (child: any, pos: any) => {
    const { maxWidth, cellWidth, maxHeight, cellHeight } = this.props;
    const maxRight = maxWidth * cellWidth;
    const maxBottom = maxHeight * cellHeight;

    return (
      <Movable
        draggableOptions={this.draggableOptions}
        onMoveStart={this.onMoveStart}
        onMove={this.onMove}
        onMoveStop={this.onMoveStop}
        top={pos.top}
        left={pos.left}
        width={pos.width}
        height={pos.height}
        maxRight={maxRight}
        maxBottom={maxBottom}
      >
        {child}
      </Movable>
    );
  };

  /**
   * Extend child element to be resizable
   * @param  {Element} child    Child element of grid item
   * @param  {Object} position  Current DOM element position of grid item
   */
  mixinResizable = (child: any, position: any) => {
    const props = this.props;
    const style = props.style;
    const minWidth = props.minWidth * props.cellWidth;
    const minHeight = props.minHeight * props.cellHeight;
    const maxRight = props.maxWidth === Infinity ? Infinity : props.maxWidth * props.cellWidth;
    const maxBottom = props.maxHeight === Infinity ? Infinity : props.maxHeight * props.cellHeight;

    const minConstraints = [minWidth, minHeight];
    const maxConstraints = [maxRight, maxBottom];

    return (
      <Resizable
        width={position.width}
        height={position.height}
        style={style}
        topResize={true}
        leftResize={true}
        rightResize={true}
        bottomResize={true}
        topLeftResize={true}
        topRightResize={true}
        bottomLeftResize={true}
        bottomRightResize={true}
        minConstraints={minConstraints}
        maxConstraints={maxConstraints}
        onResizeStop={this.onResizeStop}
        onResizeStart={this.onResizeStart}
        onResize={this.onResize}
      >
        {child}
      </Resizable>
    );
  };

  /**
   * Merge grid item's props with child's props into new child instance
   * @param  {Element} child Grid item's child element
   */
  mergeParentProps = (child: any) => {
    const { layoutItem, cellWidth, cellHeight, minWidth, minHeight } = this.props;
    const itemClass = this.props.className;

    const childClassName = classnames(itemClass, child.props.className);
    const childStyle =
      layoutItem.isFullSize || !layoutItem.autoSize
        ? merge(
            {
              height: '100%'
            },
            child.props.style
          )
        : merge(
            {
              width: 'auto',
              height: 'auto',
              display: 'inline-block'
            },
            child.props.style
          );

    const restChildProps = omit(['className', 'style'], child.props);

    const newChildProps = assignAll([
      {
        className: childClassName,
        minWidth: minWidth * cellWidth,
        minHeight: minHeight * cellHeight,
        style: childStyle,
        onMeasureChange: this.onMeasureChange
      },
      restChildProps
    ]);

    return React.cloneElement(child, newChildProps);
  };

  /**
   * Scroll to current grid item
   */
  scrollToMe = () => {
    const { layoutItem, scrollTo } = this.props;

    scrollTo({
      top: layoutItem.pxTop,
      left: layoutItem.pxLeft
    });
  };

  /**
   * Call when user resizes the grid item
   * @param  {string} eventName Resize event name
   */
  onResizeHandler = (eventName: string) => {
    return newPosition => {
      const { layoutItem } = this.props;
      const isResizing = eventName === 'onResizeStart' || eventName === 'onResize';
      layoutItem.resizing(isResizing);
      this.props[eventName] && this.props[eventName](layoutItem, newPosition);
    };
  };

  /**
   * Call when user moves the grid item
   * @param  {string} eventName Move event name
   */
  onMoveHandler = (eventName: string) => {
    return newPosition => {
      const { layoutItem } = this.props;
      const isDragging = eventName === 'onMoveStart' || eventName === 'onMove';
      layoutItem.moving(isDragging);
      this.props[eventName] && this.props[eventName](layoutItem, newPosition);
    };
  };

  /**
   * Update new position including grid position of grid item
   * when its content size changed
   * @param  {number} width  Width value of grid item
   * @param  {number} height Height value of grid item
   */
  onMeasureChange = (width: number, height: number) => {
    const { layoutItem, cellWidth, cellHeight, layoutStore, printingMode } = this.props;
    const { isFullSize, autoSize } = layoutItem;

    const actualWidth = layoutComputerUtils.roundWidth(width, cellWidth);
    const actualHeight = layoutComputerUtils.roundHeight(height, cellHeight);

    if (!isFullSize && !printingMode && autoSize && this.props.onSizeChanged) {
      this.props.onSizeChanged(layoutItem, actualWidth, actualHeight);
    }

    if (layoutStore) {
      layoutStore.resetOverlayPosition();
    }
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/GridLayout/Izenda.GridItem.js